// Shared event-application logic for the dashboard's live state -- used by
// both live WebSocket delivery (Dashboard.tsx's subscription callback) and
// backfill replay (fetched history from GET /runs/{runId}/events), so the
// two paths can never diverge in how an event mutates state. See
// specs/open/smartwater-granular-event-history.md's Approach item 5.
import {
  EvalResultPayload,
  MetricPointPayload,
  PipelineEventEnvelope,
  StepLogPayload,
  StepStatusByRole,
  StepStatusPayload,
} from './interfaces';
import { StepStat } from './StepStatsPanel';

/** Maximum step_log lines retained per (step_role, level) pair -- oldest dropped first. See applyStepLog's docstring. */
export const MAX_STEP_LOG_LINES_PER_LEVEL = 500;

/** The dashboard's full per-run live state -- one bag threaded through every applyEvent call. */
export interface DashboardState {
  stepStatuses: StepStatusByRole;
  metricPoints: MetricPointPayload[];
  stepStats: StepStat[];
  evalResult: EvalResultPayload | null;
  // Keyed by step_role -- each step's own capped, arrival-ordered log line
  // list (envelope kept whole, not just the payload, so the panel can
  // render event_id/timestamp per Approach item 5's "display every
  // event_id" requirement).
  stepLogs: Record<string, PipelineEventEnvelope[]>;
}

/** Returns a fresh, empty DashboardState -- used both for initial state and for resetting on a new run_id. */
export function emptyDashboardState(): DashboardState {
  return { stepStatuses: {}, metricPoints: [], stepStats: [], evalResult: null, stepLogs: {} };
}

/**
 * Appends one step_log event to its step's retained line list, capped per
 * (step_role, level) -- not per step overall. Per-level capping is
 * deliberate: every step_log level streams live including debug (see
 * consumerLambda's Approach item 2), so a single all-levels cap would let
 * a per-boosting-round debug flood silently evict the info/warning/error
 * lines a user with the default filter is actually looking at.
 *
 * Inserted in timestamp order, not blindly appended -- live delivery
 * (Kinesis -> Lambda -> AppSync -> WebSocket) does not guarantee strict
 * arrival order for events enqueued a few milliseconds apart (confirmed
 * live, 2026-08-05: two adjacent per-boosting-round debug lines, correctly
 * timestamped a few ms apart by train.py, arrived at the browser swapped).
 * Backfill replay already sorts before applying (sortEventsChronologically
 * + applyEvents), so this insert-sorted behavior only ever has real work
 * to do on the live path, but applies uniformly to both since applyEvent
 * doesn't know which path it's on.
 */
function applyStepLog(state: DashboardState, event: PipelineEventEnvelope): DashboardState {
  const stepRole = event.step_role ?? '_unmapped';
  const payload = event.payload as StepLogPayload;
  const existingForStep = state.stepLogs[stepRole] ?? [];
  const existingAtLevel = existingForStep.filter((e) => (e.payload as StepLogPayload).level === payload.level);
  const updatedForStep =
    existingAtLevel.length >= MAX_STEP_LOG_LINES_PER_LEVEL
      ? existingForStep.filter(
          (e) => (e.payload as StepLogPayload).level !== payload.level || e !== existingAtLevel[0],
        )
      : existingForStep;
  return {
    ...state,
    stepLogs: { ...state.stepLogs, [stepRole]: insertChronologically(updatedForStep, event) },
  };
}

/**
 * Applies one event to a DashboardState, returning a new state -- the
 * single reducer both live delivery and backfill replay call, so the two
 * paths can never apply an event differently. Pure: never mutates `state`
 * or `event`, and has no side effects (callers own event_id dedup,
 * run_id-change resets, etc. -- this function only knows how to fold one
 * event into one state bag).
 */
export function applyEvent(state: DashboardState, event: PipelineEventEnvelope): DashboardState {
  switch (event.event_type) {
    case 'step_status': {
      const payload = event.payload as StepStatusPayload;
      if (!event.step_role) return state;
      return { ...state, stepStatuses: { ...state.stepStatuses, [event.step_role]: payload.status } };
    }
    case 'metric_point': {
      const payload = event.payload as MetricPointPayload;
      // A one-off stat (record counts, split sizes, hyperparameters) has
      // no iteration -- distinguishes it from a repeatedly-sampled time
      // series like train.py's per-boosting-round val_rmse, which always
      // carries one. Routed to stepStats instead of metricPoints's
      // sparkline, since a single fact isn't a line to plot.
      if (payload.iteration == null && event.step_role) {
        return {
          ...state,
          stepStats: [
            ...state.stepStats,
            { stepRole: event.step_role, metricName: payload.metric_name, value: payload.value },
          ],
        };
      }
      return { ...state, metricPoints: [...state.metricPoints, payload] };
    }
    case 'eval_result':
      return { ...state, evalResult: event.payload as EvalResultPayload };
    case 'step_log':
      return applyStepLog(state, event);
    default:
      return state; // run_status / run_manifest events don't feed any of this dashboard's live widgets yet.
  }
}

/**
 * Applies a batch of events to a state bag, in the given array order --
 * used by backfill replay, which must sort its fetched events by
 * timestamp/event_id BEFORE calling this (query order from
 * PipelineEventsTable is step-grouped, not chronological -- see that
 * spec's Approach item 1's ordering note). Does not sort internally,
 * since callers combining backfill + buffered-live events need control
 * over the exact combined order.
 */
export function applyEvents(state: DashboardState, events: PipelineEventEnvelope[]): DashboardState {
  return events.reduce(applyEvent, state);
}

/**
 * Chronological comparator: timestamp first, event_id as a tiebreak for
 * two events sharing the same millisecond-precision timestamp string.
 * Shared by sortEventsChronologically (bulk backfill sort) and
 * insertChronologically (live single-event insert) so both order events
 * identically -- a live event and a backfilled one for the same instant
 * must always land in the same relative position, regardless of which
 * path applied them.
 */
function compareChronologically(a: PipelineEventEnvelope, b: PipelineEventEnvelope): number {
  if (a.timestamp !== b.timestamp) return a.timestamp < b.timestamp ? -1 : 1;
  return a.event_id < b.event_id ? -1 : a.event_id > b.event_id ? 1 : 0;
}

/**
 * Inserts one event into an already-sorted (by compareChronologically)
 * array at its correct position, rather than appending -- see
 * applyStepLog's docstring for why live delivery order can't be trusted.
 * O(n) scan, not a binary search: per-step log volume is capped at
 * MAX_STEP_LOG_LINES_PER_LEVEL * (number of levels), a small bound, so
 * this stays cheap without the added complexity of a binary search over
 * an array that's also being spliced.
 */
function insertChronologically(
  sorted: PipelineEventEnvelope[],
  event: PipelineEventEnvelope,
): PipelineEventEnvelope[] {
  const insertAt = sorted.findIndex((existing) => compareChronologically(event, existing) < 0);
  if (insertAt === -1) return [...sorted, event];
  return [...sorted.slice(0, insertAt), event, ...sorted.slice(insertAt)];
}

/**
 * Sorts events by timestamp (tiebreak on event_id) for chronological
 * replay -- PipelineEventsTable's query order groups by step_role first,
 * so a whole-run fetch is not already in wall-clock order. See
 * specs/open/smartwater-granular-event-history.md's Approach item 1.
 */
export function sortEventsChronologically(events: PipelineEventEnvelope[]): PipelineEventEnvelope[] {
  return [...events].sort(compareChronologically);
}
